import { fullDictionary, LANG_ORDER } from '../data/translations/dictionaries';
import { uiTranslations } from '../data/translations/ui';
import { SupportedLanguage } from '../data/translations/languages';

export interface Lesson {
  id: string;
  title: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  description: string;
  prompt: string;
  translation: string;
  keywords: string[];
  category: string;
}

export const getTranslation = (text: string, language: string): string => {
  if (language === 'English') return text;
  const translations = fullDictionary[text];
  if (translations) {
    // Find index of language
    const langIndex = LANG_ORDER.indexOf(language as any);
    if (langIndex !== -1 && translations[langIndex]) {
      return translations[langIndex];
    }
  }
  
  // Fallback to simulated translation if not in dictionary
  return `[${language}] ${text}`;
};

export const getUITranslation = (key: string, language: string): string => {
  const translationEntry = uiTranslations[key];
  if (translationEntry && translationEntry[language as SupportedLanguage]) {
    return translationEntry[language as SupportedLanguage];
  }
  // If key is not found, check if it matches one of the English values (reverse lookup for convenience, optional)
  // For now, just return the key or a default
  return key;
};

export const isRTL = (lang: string) => {
  return ['Arabic', 'Hebrew'].includes(lang);
};

export const detectBrowserLanguage = (): string => {
  if (typeof navigator === 'undefined') return 'English';
  const browserLang = navigator.language.split('-')[0];
  const langMap: Record<string, string> = {
    'en': 'English',
    'es': 'Spanish',
    'fr': 'French',
    'de': 'German',
    'zh': 'Chinese',
    'ja': 'Japanese',
    'ko': 'Korean',
    'pt': 'Portuguese',
    'it': 'Italian',
    'ru': 'Russian',
    'ar': 'Arabic',
    'he': 'Hebrew',
    'hi': 'Hindi',
    'bn': 'Bengali',
    'hu': 'Hungarian',
    'ro': 'Romanian'
  };
  return langMap[browserLang] || 'English';
};

export const reverseLookup = (text: string, language: string): { text: string, isExact: boolean } | null => {
  const lowerText = text.toLowerCase().trim();
  const cleanInput = lowerText.replace(/[.,!?;]*$/, '');
  
  // 1. If specific language (not English), search that language first
  if (language !== 'English') {
    const langIndex = LANG_ORDER.indexOf(language as any);
    if (langIndex !== -1) {
      // Exact Match
      for (const [englishKey, translations] of Object.entries(fullDictionary)) {
        const translation = translations[langIndex]?.toLowerCase();
        const cleanTranslation = translation?.replace(/[.,!?;]*$/, '');
        if (cleanTranslation === cleanInput) return { text: englishKey, isExact: true };
      }
      // Partial Match
      for (const [englishKey, translations] of Object.entries(fullDictionary)) {
        const translation = translations[langIndex]?.toLowerCase();
        if (translation?.includes(cleanInput)) return { text: englishKey, isExact: false };
      }
    }
  }

  // 2. Global Search (for English mode or fallback)
  // First, check if it matches an English key directly
  for (const englishKey of Object.keys(fullDictionary)) {
    const cleanKey = englishKey.toLowerCase().replace(/[.,!?;]*$/, '');
    if (cleanKey === cleanInput) return { text: englishKey, isExact: true };
  }

  // Then search ALL languages for exact match
  for (const [englishKey, translations] of Object.entries(fullDictionary)) {
    for (const translation of translations) {
      const cleanTranslation = translation?.toLowerCase().replace(/[.,!?;]*$/, '');
      if (cleanTranslation === cleanInput) return { text: englishKey, isExact: true };
    }
  }

  // Finally, search ALL languages for partial match
  for (const [englishKey, translations] of Object.entries(fullDictionary)) {
    for (const translation of translations) {
      if (translation?.toLowerCase().includes(cleanInput)) return { text: englishKey, isExact: false };
    }
  }
  
  // If in English mode and nothing found, return input as is (fallback)
  if (language === 'English') {
    return { text: text, isExact: true };
  }

  return null;
};
